import { useAuthStore } from '../stores/auth';
import { useProjectStore } from '../stores/project';
import { handleUnauthorizedApiResponse } from './session';

// In dev, default to the Vite proxy (/api → API, same-origin, no CORS).
// Override with VITE_API_BASE_URL for direct/cross-origin calls if needed.
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

function parseErrorMessage(raw: string, fallback: string): string {
  let message = raw;
  try {
    const parsed = JSON.parse(raw) as { detail?: unknown };
    message =
      typeof parsed.detail === 'string'
        ? parsed.detail
        : JSON.stringify(parsed.detail ?? parsed);
  } catch {
    // not JSON — keep raw text
  }
  return message || fallback;
}

async function assertOk(res: Response, path: string): Promise<void> {
  if (res.ok) return;
  const raw = await res.text();
  handleUnauthorizedApiResponse(res.status, path);
  throw new Error(`HTTP ${res.status}: ${parseErrorMessage(raw, res.statusText)}`);
}

async function request<T>(
  path: string,
  method: Method,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const token = useAuthStore.getState().token;
  const projectId = useProjectStore.getState().activeProjectId;
  const headers: Record<string, string> = {
    'content-type': 'application/json',
  };
  if (token) headers.authorization = `Bearer ${token}`;
  if (projectId) headers['X-Project-Id'] = projectId;

  const init: RequestInit = { method, headers };
  if (body !== undefined) init.body = JSON.stringify(body);
  if (signal) init.signal = signal;

  const res = await fetch(`${BASE_URL}${path}`, init);
  await assertOk(res, path);

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, 'GET', undefined, signal),
  post: <T>(path: string, body: unknown) => request<T>(path, 'POST', body),
  put: <T>(path: string, body: unknown) => request<T>(path, 'PUT', body),
  patch: <T>(path: string, body: unknown) => request<T>(path, 'PATCH', body),
  delete: <T>(path: string) => request<T>(path, 'DELETE'),

  /** POST a FormData body (file uploads) — skips JSON serialisation and content-type header. */
  postForm: async <T>(path: string, form: FormData): Promise<T> => {
    const token = useAuthStore.getState().token;
    const projectId = useProjectStore.getState().activeProjectId;
    const headers: Record<string, string> = {};
    if (token) headers.authorization = `Bearer ${token}`;
    if (projectId) headers['X-Project-Id'] = projectId;
    const res = await fetch(`${BASE_URL}${path}`, { method: 'POST', headers, body: form });
    await assertOk(res, path);
    return res.json() as Promise<T>;
  },
};
